import { AnalyticsManager } from '@oy/analytics';
import { useDispatch } from 'react-redux';
import {
  GET_FRONTEND_CONFIG_URL,
  getChildType,
  getFrontendConfigs,
  getParent,
  getPaymentInfo,
  getProfile,
  postCheckTxCategory,
} from 'src/clients';
import { AccountType, IS_DANARAPAY } from 'src/constants';
import { useDomainEnv, useSwitchEnv } from 'src/hooks';
import { loadProductConfigs, loadProductStatus } from 'src/redux/actions';
import {
  clearAuth,
  clearAuthOtp,
  setAuth,
  setAuthOtp,
  setAuthUser,
  setDefaultUser,
  setShowOtpModal,
} from 'src/redux/actions/authActions';
import { fetchRootFirebaseData } from 'src/utilities/Firebase/firebase';
import { storage } from 'src/utilities/Storage';
import moment from 'src/utilities/moment';
import { reportHookError } from 'src/utilities/sentry';
import { mutate } from 'swr';
import { usePermissionData } from './permission';

type SetLoginDataParams = {
  password_expired: boolean;
  payment_services: any;
  token: string;
  username: string;
  wallet_info: any;
  profile_pict: string | null;
  session_id: string | null;
  trust_token: string | null;
};

type SetOtpDataParams = {
  username: string;
  intermediaryToken: string;
  email: string | null;
};

export const useLoginStaging = () => {
  const dispatch = useDispatch();

  const { switchStagingLogin } = useSwitchEnv();
  const { isDomainProdEnv } = useDomainEnv();
  const { fetchPermission } = usePermissionData();

  const fetchPaymentInfo = async () => {
    const res = await getPaymentInfo();
    return res.data.data!;
  };

  const fetchUserProfile = async () => {
    const res = await getProfile();
    return res.data;
  };

  const setLoginData = async (params: SetLoginDataParams) => {
    mutate(import.meta.env.VITE_FIREBASE_DATABASE_URL, fetchRootFirebaseData);
    mutate(GET_FRONTEND_CONFIG_URL, getFrontendConfigs);
    AnalyticsManager.instance().setSessionId(params.session_id ?? '');
    dispatch(
      setAuth({
        authData: {
          password_expired: params.password_expired,
          payment_services: params.payment_services,
          profile_pict: params.profile_pict,
          session_id: params.session_id,
          token: params.token,
          wallet_info: params.wallet_info,
        },
        userData: {
          username: params.username,
        },
      }),
    );

    if (params.trust_token) {
      const trustToken = JSON.parse(storage.get('trustToken', '{}'));
      storage.set(
        'trustToken',
        JSON.stringify({
          ...trustToken,
          [params.username]: params.trust_token,
        }),
      );
    }
  };

  const fetchPostLogin = async () => {
    try {
      const [{ data: childData }, { data: parentData }] = await Promise.all([
        getChildType(),
        getParent(),
      ]);
      if (childData.success && parentData.success) {
        const hasParent =
          parentData != null && parentData?.data?.parentUsername !== '';
        const isViewOnly = childData?.data?.childType === 'VIEWONLY';
        if (!(hasParent && isViewOnly)) {
          await postCheckTxCategory();
        }
      }
    } catch {
      // TODO: catch exception
    }
  };

  const triggerLoginStaging = async (params: {
    username: string;
    shouldCheckCreatedTime?: boolean;
  }) => {
    try {
      const userProfile = (await fetchUserProfile()) ?? null;
      await fetchPostLogin();
      await fetchPermission();
      dispatch(setAuthUser(userProfile));
      dispatch(setDefaultUser());
      dispatch(loadProductConfigs());
      dispatch(loadProductStatus());

      const { accountType, b2xUser } = await fetchPaymentInfo()!;

      if (
        params.shouldCheckCreatedTime &&
        Date.now().valueOf() - moment(b2xUser.created).valueOf() >
          24 * 3600 * 1000
      ) {
        storage.set('triggerUpgradeTooltips', true);
      }

      if (
        isDomainProdEnv &&
        accountType !== AccountType.Approved &&
        !IS_DANARAPAY
      ) {
        await switchStagingLogin(params.username);
      }
    } catch (error: any) {
      dispatch({ type: 'RESET_ENV' });
      dispatch(clearAuth());

      reportHookError({
        name: 'useLoginStaging',
        message: error.message,
        domain: 'growth',
        exception: error,
      });

      throw new Error(error.message ?? 'Something went wrong.');
    }
  };

  const resetAuthData = () => {
    dispatch(clearAuth());
  };

  const setOtpData = (params: SetOtpDataParams) => {
    const { username, intermediaryToken, email } = params;
    dispatch(
      setAuthOtp({
        username,
        intermediaryToken,
        email,
      }),
    );
  };

  const resetOtpData = () => {
    dispatch(clearAuthOtp());
  };

  const showOtpModal = (value: boolean) => {
    dispatch(setShowOtpModal(value));
  };

  return {
    actions: {
      resetAuthData,
      resetOtpData,
      setLoginData,
      setOtpData,
      showOtpModal,
      triggerLoginStaging,
    },
  };
};
